import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useEffect } from 'react';
import { Spacer, FieldSet, Flex, Label, Popper, FormHelp, AdornedInput, Adornment, Input, Popover, Heading, Paragraph } from '@zillow/constellation';
import { event } from '@zillow/universal-analytics/src/datalayer';
import { useFormContext, useFormState } from 'react-hook-form';
import { FieldConfig, isSameFloat } from './VALoanCalculator.utils.js';
import { getCommaSeparatedNumber, commaSeparateNumber } from '../../utils/zhl-calculator.js';
import { handleFieldChangeEvent } from './VALoanCalculator.Clickstream.js';
import useValidFieldUpdate from '../../utils/form/useValidFieldUpdate.js';

function PropertyTaxField() {
  var _proxyFormState$error, _proxyFormState$error2;
  var form = useFormContext();
  var proxyFormState = useFormState();
  var error = (proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error = proxyFormState.errors[FieldConfig.taxRate.name]) === null || _proxyFormState$error === void 0 ? void 0 : _proxyFormState$error.message) || (proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error2 = proxyFormState.errors[FieldConfig.taxRate.name]) === null || _proxyFormState$error2 === void 0 ? void 0 : _proxyFormState$error2.message);
  var fieldAmount = form.register(FieldConfig.taxRate.name, FieldConfig.taxRate.config);
  var fieldPercent = form.register(FieldConfig.taxRatePercent.name, FieldConfig.taxRatePercent.config);
  var amountInputRef = useRef(null);
  var formatAmount = commaSeparateNumber;
  var onOpenInteractionEvent = {
    envelope: {
      event_type_id: '5030',
      event_type_version_id: '1',
      event_template_id: '297',
      event_template_version_id: '1'
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'cms',
      trigger_source_nm: 'cms_tooltip',
      trigger_object_nm: 'no_trigger_object'
    },
    semantic: {
      semantic_event_nm: 'cms_open'
    },
    cms_info: {
      block_type_cd: 'tooltip',
      block_id: 'va_mortgage_calc',
      block_displayed_txt: 'Property Tax'
    }
  };
  var onCloseInteractionEvent = {
    envelope: {
      event_type_id: '2051',
      event_type_version_id: '1',
      event_template_id: '126',
      event_template_version_id: '1'
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'cms',
      trigger_source_nm: 'cms_tooltip',
      trigger_object_nm: 'no_trigger_object'
    },
    semantic: {
      semantic_event_nm: 'cms_close'
    },
    cms_info: {
      block_type_cd: 'tooltip',
      block_id: 'va_mortgage_calc',
      block_displayed_txt: 'Property Tax'
    }
  };

  // Format on mount
  useEffect(function () {
    if (formatAmount) {
      var defaultAmountValue = form.getValues(fieldAmount.name);
      if (defaultAmountValue) {
        amountInputRef.current.value = formatAmount(defaultAmountValue);
      }
    }
  }, []);
  useSyncTaxRatePercent();
  var trigger = /*#__PURE__*/React.createElement(Popover, {
    header: /*#__PURE__*/React.createElement(Heading, {
      level: 6
    }, "Property Tax"),
    body: /*#__PURE__*/React.createElement(Paragraph, null, "The VA calculator includes estimated property taxes. The value represents an annual tax on the homeowner's property and the tax amount is based on the home's value. To convert an annual property tax in dollars to a percentage, divide the property tax by your home value and then multiply by 100.")
  });
  return /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(FieldSet, {
    legend: /*#__PURE__*/React.createElement(Flex, {
      display: "flex",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Label, {
      htmlFor: "taxRateAmmount"
    }, "Property tax"), "\xA0", /*#__PURE__*/React.createElement(Popper, {
      triggered: trigger,
      onOpen: function onOpen() {
        return event(onOpenInteractionEvent);
      },
      onClose: function onClose() {
        return event(onCloseInteractionEvent);
      }
    })),
    description: /*#__PURE__*/React.createElement(FormHelp, {
      error: Boolean(error)
    }, error || /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0"))
  }, /*#__PURE__*/React.createElement(Flex, {
    display: "flex"
  }, /*#__PURE__*/React.createElement(AdornedInput, {
    style: {
      flex: '1'
    },
    error: Boolean(error),
    leftAdornment: /*#__PURE__*/React.createElement(Adornment, null, "$"),
    input: /*#__PURE__*/React.createElement(Input, _extends({
      style: {
        'border-top-right-radius': 0,
        'border-bottom-right-radius': 0,
        'border-right-width': 0
      },
      id: "taxRateAmmount"
    }, fieldAmount, {
      ref: function ref(el) {
        amountInputRef.current = el;
        fieldAmount.ref(el);
      },
      onInput: formatAmount ? function (e) {
        e.target.value = formatAmount(e.target.value);
      } : undefined,
      autoComplete: "off"
    }))
  }), /*#__PURE__*/React.createElement(AdornedInput, {
    style: {
      flex: '0.5'
    },
    error: Boolean(error),
    rightAdornment: /*#__PURE__*/React.createElement(Adornment, null, "%"),
    input: /*#__PURE__*/React.createElement(Input, _extends({}, fieldPercent, {
      autoComplete: "off",
      style: {
        'border-top-left-radius': 0,
        'border-bottom-left-radius': 0
      }
    }))
  }))));
}
function useSyncTaxRatePercent() {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, function (name, value) {
    var _Object$assign;
    lastValidFields.current = Object.assign({}, lastValidFields.current, (_Object$assign = {}, _Object$assign[name] = value, _Object$assign));

    // 1) on change of taxRatePercent, update taxrate based on taxRatePercent
    if (name === FieldConfig.taxRatePercent.name) {
      var nextTaxRate = lastValidFields.current[FieldConfig.taxRatePercent.name] * lastValidFields.current[FieldConfig.homePrice.name] / 100 || 0;
      if (!isSameFloat(nextTaxRate, lastValidFields.current.taxRate)) {
        form.setValue(FieldConfig.taxRate.name, getCommaSeparatedNumber(nextTaxRate), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      handleFieldChangeEvent('Property tax percent', 'form_field');
    }

    // 2) on change of taxRate, update taxRatePercent
    if (name === FieldConfig.taxRate.name) {
      var _nextTaxRate = lastValidFields.current[FieldConfig.taxRate.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100 || 0;
      if (!isSameFloat(_nextTaxRate, lastValidFields.current.taxRatePercent)) {
        form.setValue(FieldConfig.taxRatePercent.name, _nextTaxRate, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      handleFieldChangeEvent('Property tax amount', 'form_field');
    }

    // 3) on change of homePrice, update taxRate based on taxRatePercent
    if (name === FieldConfig.homePrice.name) {
      var nextTaxrate = lastValidFields.current[FieldConfig.taxRatePercent.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name];
      if (!isSameFloat(nextTaxrate, lastValidFields.current.taxRate)) {
        form.setValue(FieldConfig.taxRate.name, getCommaSeparatedNumber(nextTaxrate), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  });
}

export { PropertyTaxField as default };
